export function toISODate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function parseISODate(value) {
  if (!value) return null;
  const [year, month, day] = String(value).split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

export function addDays(isoDate, days) {
  const date = parseISODate(isoDate);
  if (!date) return isoDate;
  date.setDate(date.getDate() + days);
  return toISODate(date);
}

export function localDayStartISO(isoDate) {
  const date = parseISODate(isoDate);
  if (!date) return undefined;
  return date.toISOString();
}

export function localDayEndExclusiveISO(isoDate) {
  const date = parseISODate(isoDate);
  if (!date) return undefined;
  date.setDate(date.getDate() + 1);
  return date.toISOString();
}

export function queryDateParams(dateFrom, dateTo) {
  const params = {};
  if (dateFrom) params.dateFrom = localDayStartISO(dateFrom);
  if (dateTo) params.dateTo = localDayEndExclusiveISO(dateTo);
  return params;
}

function startOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function endOfMonth(date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function weekendBounds(now = new Date()) {
  const day = now.getDay();
  const saturday = new Date(now);
  if (day === 0) {
    saturday.setDate(now.getDate() - 1);
  } else if (day !== 6) {
    saturday.setDate(now.getDate() - day - 1);
  }
  const sunday = new Date(saturday);
  sunday.setDate(saturday.getDate() + 1);
  return [saturday, sunday];
}

export function rangeForPreset(preset, now = new Date()) {
  const today = toISODate(now);
  if (preset === 'today') return { preset, dateFrom: today, dateTo: today };
  if (preset === 'yesterday') {
    const yesterday = addDays(today, -1);
    return { preset, dateFrom: yesterday, dateTo: yesterday };
  }
  if (preset === 'weekend') {
    const [saturday, sunday] = weekendBounds(now);
    return { preset, dateFrom: toISODate(saturday), dateTo: toISODate(sunday) };
  }
  if (preset === 'week') {
    return { preset, dateFrom: addDays(today, -6), dateTo: today };
  }
  if (preset === 'month') {
    return { preset, dateFrom: toISODate(startOfMonth(now)), dateTo: toISODate(endOfMonth(now)) };
  }
  if (preset === 'year') {
    return {
      preset,
      dateFrom: `${now.getFullYear()}-01-01`,
      dateTo: `${now.getFullYear()}-12-31`
    };
  }
  return { preset: 'all', dateFrom: '', dateTo: '' };
}

export function detectPreset(dateFrom, dateTo, now = new Date()) {
  const presets = ['today', 'yesterday', 'weekend', 'week', 'month', 'year', 'all'];
  return presets.find((preset) => {
    const range = rangeForPreset(preset, now);
    return range.dateFrom === (dateFrom || '') && range.dateTo === (dateTo || '');
  }) || 'custom';
}

export function isSingleDay(dateFrom, dateTo) {
  return Boolean(dateFrom && dateTo && dateFrom === dateTo);
}

export function formatDayLabel(isoDate) {
  const date = parseISODate(isoDate);
  if (!date) return '';
  return date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

export function formatRangeLabel(preset, dateFrom, dateTo) {
  if (preset === 'all' || (!dateFrom && !dateTo)) return 'All time';
  if (isSingleDay(dateFrom, dateTo)) {
    const today = toISODate(new Date());
    if (dateFrom === today) return `Today · ${formatDayLabel(dateFrom)}`;
    if (dateFrom === addDays(today, -1)) return `Yesterday · ${formatDayLabel(dateFrom)}`;
    return formatDayLabel(dateFrom);
  }
  if (preset === 'weekend') return `Weekend · ${formatDayLabel(dateFrom)}–${formatDayLabel(dateTo)}`;
  if (preset === 'week') return 'Last 7 days';
  if (preset === 'month' && dateFrom) {
    const date = parseISODate(dateFrom);
    return date.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  }
  if (preset === 'year' && dateFrom) return dateFrom.slice(0, 4);
  if (dateFrom && dateTo) return `${formatDayLabel(dateFrom)} – ${formatDayLabel(dateTo)}`;
  if (dateFrom) return `From ${formatDayLabel(dateFrom)}`;
  return `Through ${formatDayLabel(dateTo)}`;
}

export function rangeSpanDays(dateFrom, dateTo) {
  if (!dateFrom || !dateTo) return Infinity;
  const start = parseISODate(dateFrom);
  const end = parseISODate(dateTo);
  return Math.round((end - start) / 86400000) + 1;
}
